import { QUERY_KEYS } from "@config/constant";
import type { RecommendQuestionRequest } from "@/src/services/recommendQuestionsService";
import type { StockType } from "@utils/stockType";

/**
 * 首页所有数据查询的 QueryKey 统一管理（兼容 stock 和 crypto 页面）
 *
 * 目的：
 * 1. 集中管理所有 QueryKey，便于检查数据依赖关系
 * 2. 确保 SSR 和客户端使用完全一致的 QueryKey
 * 3. 清晰标注每个查询的依赖项（stockType, locale, isLoggedIn 等）
 * 4. 方便排查切换市场或登录状态时哪些数据需要刷新
 *
 * 数据依赖说明：
 * - stockType: 仅在 type="stock" 时有效，切换 US/HK 市场时刷新
 * - locale: marketSummary, hotNews（切换语言时刷新）
 * - isLoggedIn: marketSummary, hotNews（登录/登出时刷新）
 * - type: themesHeatmap, marketSummary, recommendQuestion, hotNews（stock/crypto 页面切换时刷新）
 *
 * 注意：crypto 页面不使用 stockType，相关查询不会因 stockType 变化而刷新
 */
export const homeQueryKeys = {
  // ==================== SSR 预取数据 ====================

  /**
   * Market Indicator（市场指标）
   *
   * @依赖 type - stock/crypto 页面切换时刷新
   * @依赖 stockType - 仅 stock 页面有效，切换 US/HK 市场时刷新
   * @用途 SSR 预取 + 客户端 hydration
   * @使用位置 useStockHomeData hook, MarketIndicators 组件
   */
  marketIndicator: (type: "stock" | "crypto", stockType?: StockType) =>
    [QUERY_KEYS.STOCK_HOME_MARKET_INDICATOR, type, stockType] as const,

  /**
   * Indices Trading Data（指数交易数据）
   *
   * @依赖 type - stock/crypto 页面切换时刷新
   * @依赖 stockType - 仅 stock 页面有效，切换 US/HK 市场时刷新
   * @用途 SSR 预取 + 客户端 hydration
   * @使用位置 useStockHomeData hook, IndicesList 组件
   */
  indicesTradingData: (type: "stock" | "crypto", stockType?: StockType) =>
    [QUERY_KEYS.STOCK_HOME_INDICES_TRADING_DATA, type, stockType] as const,

  /**
   * Themes In Play（投资主题数据）
   *
   * @依赖 type - stock/crypto 页面切换时刷新
   * @依赖 locale - 切换语言时刷新（需要本地化主题名称和描述）
   * @用途 SSR 预取 + 客户端 hydration
   * @使用位置 ThemesInPlay 组件
   */
  themesInPlay: (type: "stock" | "crypto", locale: string) =>
    [QUERY_KEYS.HOME_THEMES_IN_PLAY, type, locale] as const,

  /**
   * Featured CIK 大佬持仓列表（SSR 预取专用 key）
   *
   * @依赖 locale - 切换语言时刷新
   * @用途 SSR 用 prefetchQuery 存 plain Cik[]，再通过 getInitialData 取出作为 prop，
   *       交由客户端 useCikInfiniteQuery 的 initialData 桥接。
   * @注意 刻意与客户端 infinite query 的 4 段 key 不同，避免 infinite query 读到
   *       plain-array 形状的水合数据导致崩溃（与 opportunities 模块同一模式）。
   */
  featuredCikList: (locale: string) =>
    [QUERY_KEYS.HOME_FEATURED_CIK_LIST, locale] as const,

  articles: (locale: string, market?: string) =>
    [QUERY_KEYS.ARTICLES, locale, market] as const,

  // ==================== 客户端专属数据 ====================

  /**
   * Market Summary（市场概览）
   *
   * @依赖 locale - 切换语言时刷新
   * @依赖 type - stock/crypto 页面切换时刷新
   * @依赖 isLoggedIn - 登录/登出时刷新（需要用户信息）
   * @依赖 stockType - 仅 stock 页面有效，切换 US/HK 市场时刷新
   * @用途 客户端获取（SSR 无法获取用户登录状态）
   * @使用位置 useMarketSummary hook, MarketSummary 组件
   */
  marketSummary: (
    locale: string,
    type: "stock" | "crypto",
    isLoggedIn: boolean,
    stockType?: StockType,
  ) =>
    [
      QUERY_KEYS.MARKET_SUMMARY,
      locale,
      type,
      isLoggedIn,
      type === "stock" ? stockType : undefined,
    ] as const,

  /**
   * Themes Heatmap（主题热力图）
   *
   * @依赖 type - stock/crypto 页面切换时刷新
   * @依赖 stockType - 仅 stock 页面有效，切换 US/HK 市场时刷新
   * @依赖 locale - 切换语言时刷新（主题名称本地化）
   * @用途 客户端获取
   * @使用位置 useThemesHeatmapData hook, ThemesHeatmap 组件
   */
  themesHeatmap: (
    type: "stock" | "crypto",
    stockType: StockType | undefined,
    locale: string,
  ) =>
    [
      QUERY_KEYS.THEMES,
      "heatmap",
      type,
      type === "stock" ? stockType : undefined,
      locale,
    ] as const,

  /**
   * Recommend Questions（推荐问题）
   *
   * @依赖 type - stock/crypto 页面切换时刷新（通过 recommendRequest）
   * @依赖 stockType - 仅 stock 页面有效，切换 US/HK 市场时刷新
   * @依赖 recommendRequest - 包含 scope 和 limit 信息
   * @依赖 key - 可选的额外区分键（如 scope key）
   * @用途 客户端获取
   * @使用位置 useRecommendQuestion hook, HomePageSender 组件
   */
  recommendQuestion: (
    type: "stock" | "crypto",
    recommendRequest: RecommendQuestionRequest,
    key: string | undefined,
    stockType?: StockType,
  ) =>
    [
      QUERY_KEYS.RECOMMEND_QUESTION,
      recommendRequest,
      key,
      type === "stock" ? stockType : undefined,
    ] as const,

  // ==================== Crypto 专属数据 ====================

  /**
   * Top Crypto Tokens（热门加密货币）
   *
   * @依赖 无 - 固定返回 BTC/ETH/SOL/BNB
   * @用途 SSR 预取 + 客户端 hydration
   * @使用位置 IndicesList 组件（crypto 模式）
   */
  topCryptoTokens: () => ["home-crypto-top-tokens"] as const,
};
/**
   *  360 Report（360 报告）
   *  Top Gainers & Losers（涨幅榜和跌幅榜）
   *  Earnings Calendar（财报日历）
   *  未处理
   * /

/**
 * 类型定义：所有 QueryKey 的类型
 */
